//react
import clsx from 'clsx'
import { useState, useEffect, useContext } from 'react'
import GlobalContext from '../../../../../../context/global-context'

//utils
import { formatCurrency, formatDate, formatRut } from '../../../../../utils/formatter'
import { getDocumentPdfKey, getDocumentPdfLookup } from '../../../../../../utils/documentPdfKey'
import { getCurrentDate } from '../../../../../utils/constant'

//ui
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import BlockIcon from '@mui/icons-material/Block'
import SearchIcon from '@mui/icons-material/Search'
import iconoEnlace from '../../../../../../assets/iconos/iconEnlace.png'
//api
import { OrganizationAPI } from '../../../../../../api/organization.api'
import businessApi from '../../../../../../api/business.api'

//components
import { colsIngresos as cols } from '../../../../data/headersTables'
import { Text } from '../../../../ui'
import { Spinner } from '../../../../ui/Spinner'
// import CustomPagination from '../../../../table/CustomPagination'
import ModalDialogMotionEditForm from '../../../../modalDialogEditarMovimiento/ModalDialogMotionEditForm'
import { Link, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import translations from '../../../../ui/traslation/traslationAdministracion.json'
import ModalViewAdministracion from '../../../../ui/tables/baseTable/ModalViewAdministracion'
//assets
import isologo_SII from '../../../../../../assets/isologo_SII.png'
import iconNuevo from '../../../../../../assets/iconos/iconNuevo.png'
import StarIcon from '@mui/icons-material/StarOutlineOutlined'
import StarFullIcon from '@mui/icons-material/StarOutlined'
import { TablesAPI } from '../../../../../../api/table.api'
import CustomButton from '../../../../ui/CustomButton'
import ModalEditConciliar from '../../../../modalDialogEditarMovimiento/ModalEditConciliar'
import { activosFijos, activosIntangibles, existencias } from '../../../../data/dataProveedor'

const resolveExpenseAccountType = accountId => {
  const numericId = Number(accountId)
  if (existencias.includes(numericId)) return 'existensias'
  if (activosFijos.includes(numericId)) return 'activos-fijos'
  if (activosIntangibles.includes(numericId)) return 'activos-intangibles'
  return 'gastos'
}

const normalizeAccountText = value =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

const TableEgresos = ({ rows, getData, rowsPerPage, highlightedRows, toggleHighlight, setData, onSort, order}) => {
  const [page] = useState(0)
  const organizationAPI = new OrganizationAPI()
  const { ui, currentBusiness } = useContext(GlobalContext)

  // variables de estado para modal de edicion
  const [open, setOpen] = useState(false)
  //const [type, setType] = useState(null)
  const [dataModal, setDataModal] = useState({
    idLibro: '',
    status: '',
    aprobado: '',
    documentType: ''
  })

  // variable para llamar a getdata desde el modal
  const [switchGetData, setSwitchGetData] = useState(false)
  const [currentDate] = useState(getCurrentDate)
  const tablesAPI = new TablesAPI()
  const [showPopup, setShowPopup] = useState(false)
  const [pendingApproval, setPendingApproval] = useState(null)
  const [, setIsCheckboxChecked] = useState(false)
  const [openComprobante, setOpenComprobante] = useState(false)
  const [openConciliar, setOpenConciliar] = useState(false)
  const [dataComprobante, setDataComprobante] = useState({
    libroId: '',
    nombreFolio: '',
    folio: ''
  })
  const [pdfLoadingId, setPdfLoadingId] = useState(null)
  const [pdfAvailabilityByKey, setPdfAvailabilityByKey] = useState({})

  const handleComprobante = (libroId, nombreFolio, folio) => {
    setDataComprobante({libroId, nombreFolio, folio})
    setOpenComprobante(true)
  }

  const isHonorarioRow = row => row?.nombreFolio === 'Boleta'
  const getPdfSummaryKey = row => (isHonorarioRow(row) ? `${row?.query}:${row?.numeroFolio}` : getDocumentPdfKey(row))

  const handleOpenDocumentoPdf = async row => {
    if (!row?.query || !row?.numeroFolio || pdfLoadingId) return
    setPdfLoadingId(row.id)
    try {
      const { data } = isHonorarioRow(row)
        ? await businessApi.getHonorarioDocumentPdfUrl({
            businessId: currentBusiness.id,
            query: row.query,
            folio: row.numeroFolio
          })
        : await businessApi.getCompraDocumentPdfUrl({
            businessId: currentBusiness.id,
            ...getDocumentPdfLookup(row)
          })
      if (data?.pdfUrl) {
        window.open(data.pdfUrl, '_blank', 'noopener,noreferrer')
      }
    } catch (error) {
      console.error('Error al obtener el PDF del documento:', error)
    } finally {
      setPdfLoadingId(null)
    }
  }

  useEffect(() => {
    const rowsWithPdf = (rows || []).filter(row => row?.query && row?.numeroFolio)
    const compraQueries = Array.from(new Set(rowsWithPdf.filter(row => !isHonorarioRow(row)).map(row => row.query)))
    const honorarioQueries = Array.from(new Set(rowsWithPdf.filter(row => isHonorarioRow(row)).map(row => row.query)))
    if (
      !currentBusiness?.id ||
      !currentBusiness?.rutRepresentante ||
      (compraQueries.length === 0 && honorarioQueries.length === 0)
    ) return

    let cancelled = false
    const requests = []
    if (compraQueries.length > 0) {
      requests.push(businessApi.getCompraDocumentPdfUrlsSummary({ businessId: currentBusiness.id, queries: compraQueries }))
    }
    if (honorarioQueries.length > 0) {
      requests.push(businessApi.getHonorarioDocumentPdfUrlsSummary({ businessId: currentBusiness.id, queries: honorarioQueries }))
    }
    Promise.all(requests)
      .then(responses => {
        if (cancelled) return
        const merged = responses.reduce((acc, { data }) => ({ ...acc, ...data }), {})
        setPdfAvailabilityByKey(prev => ({ ...prev, ...merged }))
      })
      .catch(error => console.error('Error al obtener el resumen de PDFs:', error))

    return () => {
      cancelled = true
    }
  }, [rows, currentBusiness?.id])

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const styles = {
    table: 'w-full min-w-[980px] table-auto text-[0.65rem] sm:text-[0.725rem] border-collapse leading-tight font-sans',
    thead: 'sticky top-0 z-10 bg-white',
    th: 'py-2 px-1 sm:px-2 w-auto max-w-[120px] text-center break-words border-b border-gray-200 uppercase text-[0.55rem] sm:text-[0.6rem] font-bold text-gray-500',
    tr: 'border-b border-gray-100 hover:bg-gray-50 transition-colors',
    td: 'py-2 px-1 sm:px-2 w-auto max-w-[200px] text-center break-words align-middle',
    smallCol: 'w-auto max-w-[70px] sm:max-w-[80px]',
    wideCol: 'w-auto max-w-[220px] sm:max-w-[300px]',
    boldText: 'font-bold text-[0.62rem] sm:text-[0.65rem]',
    text: 'text-[0.65rem] sm:text-[0.705rem] text-gray-400 font-medium',
    pdfLink: 'text-[0.65rem] sm:text-[0.705rem] text-blue-600 hover:text-blue-800 hover:underline font-medium',
    dateHeader: 'bg-gray-50/80 backdrop-blur-sm border-y border-gray-200/50 py-2 px-4 text-left font-bold text-primary flex items-center gap-2 cursor-pointer hover:bg-gray-100 transition-colors group font-sans'
  }

  const resolveLibroType = documentType => (documentType === 'Boleta' ? 'honorario' : 'compra')

  const handleClickOpen = (idLibro, status, documentType = '') => {
    setDataModal(prev => ({ ...prev, idLibro, status, documentType }))
    setOpen(true)
  }

  const handleClickConciliar = (idLibro, status, documentType = '') => {
    setDataModal(prev => ({ ...prev, idLibro, status, documentType }))
    setOpenConciliar(true)
  }

  /*const handleClickApproved = async data => {
    ui.setLoader({ visible: true, text: 'editando datos' })
    const updateData = {
      cuentaContableId: null,
      glosa: '',
      vencimiento: data.fechaVencimiento,
      validation: 1
    }
    try {
      const response = await organizationAPI.patchDataLibroContable(
        currentBusiness.id,
        data.id,
        updateData
      )
      await getData()
      console.info(response)
    } catch (error) {
      console.error(error)
    } finally {
      ui.setLoader({ visible: false, text: '' })
    }
  }*/

  const orderableCols = [
    { col: '', field: null },
    { col: 'T. Doc/Folio', field: 'numeroFolio' },
    { col: 'Cliente', field: 'razonSocial' },
    { col: 'Fecha', field: 'fecha' },
    { col: 'Clasificación', field: 'nombreCuenta' },
    { col: 'Centro Costo', field: 'centroCosto' },
    { col: 'Monto', field: 'montoTotal' },
    { col: 'Status', field: 'status' },
    { col: 'Acciones', field: 'aprobado' }
  ]

  useEffect(() => {
    if (!switchGetData) return
    getData()
    setSwitchGetData(false)
  }, [switchGetData])

  const obtenerAbreviatura = nombre => {
    return translations.documentosContables[nombre] || nombre // Si no encuentra la abreviatura, devuelve el nombre original.
  }

  /*const handleCheckboxClick = (event) => {
    event.stopPropagation()
    event.preventDefault()
    const allApproved = rows.every(item => item.aprobado === 1)
    setPendingApproval(!allApproved)
    setShowPopup(true)
  }*/

  const confirmAction = async () => {
    setShowPopup(false)
    const newData = rows.map(item => ({
        ...item,
        aprobado: pendingApproval ? 1 : 0,
    }))
    setData(newData);
    setPendingApproval(null);
    if (currentBusiness.id) {
        try {
            const ids = rows.map(item => item.id)
            await tablesAPI.patchApproveAll(currentBusiness.id, ids, pendingApproval ? 1 : 0)
        } catch (error) {
            console.error(error)
        }
    }
  }

  useEffect(() => {
      setIsCheckboxChecked(rows.every(item => item.aprobado === 1))
  }, [rows])

  const handleNoConsiderarClick = async (row) => {
    try {
      const response = await organizationAPI.getDataLibroContable(
        currentBusiness.id,
        row.id,
        'compra'
      )
      const cuentaData = response.data
      if (!cuentaData) return

      let cuentaContableId =
        cuentaData?.id_key ??
        cuentaData?.idKey ??
        cuentaData?.cuentaContable?.id_key ??
        cuentaData?.cuentaContable?.idKey

      if (cuentaContableId == null) {
        const currentAccountId = cuentaData?.idCuenta ?? row?.cuentaContableId
        const preferredType = resolveExpenseAccountType(currentAccountId)
        const accountTypes = [
          preferredType,
          ...['gastos', 'existensias', 'activos-fijos', 'activos-intangibles'].filter(
            type => type !== preferredType
          )
        ]
        const accountResponses = await Promise.allSettled(
          accountTypes.map(type =>
            organizationAPI.getCuentasContables({ businessId: currentBusiness.id, type })
          )
        )
        const accounts = accountResponses.flatMap(result =>
          result.status === 'fulfilled' && Array.isArray(result.value?.data)
            ? result.value.data
            : []
        )
        const detailIds = [
          cuentaData?.idCuenta,
          cuentaData?.cuentaContableId,
          cuentaData?.cuentaContable?.id,
          row?.cuentaContableId
        ]
          .filter(value => value != null)
          .map(String)
        const detailNames = [
          cuentaData?.nombreCuenta,
          cuentaData?.cuentaContable?.nombreCuenta,
          cuentaData?.cuentaContable?.name,
          row?.nombreCuenta
        ]
          .map(normalizeAccountText)
          .filter(Boolean)
        const account = accounts.find(item =>
          [item?.id, item?.cuentaContableId, item?.cuentaCustomId]
            .filter(value => value != null)
            .some(value => detailIds.includes(String(value)))
        ) ?? accounts.find(item =>
          [item?.name, item?.nombreCuenta, item?.nameCuenta]
            .map(normalizeAccountText)
            .some(value => value && detailNames.includes(value))
        )

        cuentaContableId = account?.id_key ?? account?.idKey
      }

      if (cuentaContableId == null) {
        throw new Error('No fue posible resolver el id_key de la cuenta contable del egreso')
      }

      const submitData = {
        idCuenta: cuentaContableId,
        glosa: cuentaData.glosa,
        fechaVencimiento: cuentaData.fechaVencimiento,
        validation: -5
      }

      await handleSubmit(row.id, submitData)

    } catch (error) {
      console.error('Error al procesar No Considerar:', error)
    }
  }

  const handleSubmit = async (idLibro, data) => {
    try {
      ui.setLoader({ visible: true })
      const updateData = {
        cuentaContableId: data.idCuenta,
        glosa: data.glosa,
        vencimiento: data.fechaVencimiento,
        validation: data.validation
      }
      const response = await organizationAPI.patchDataLibroContable(
        currentBusiness?.id,
        idLibro,
        updateData
      )
      console.info(response)
      setSwitchGetData(true) // Refresca tabla
    } catch (error) {
      console.error(error)
    } finally {
      ui.setLoader({ visible: false })
    }
  }

  const getCleanStatus = row =>
    typeof row?.status === 'string'
      ? row.status.replace(/\s*undefined\s*/gi, '').trim()
      : ''

  const normalizeStatus = status =>
    String(status || '')
      .replace(/\s*undefined\s*/gi, '')
      .trim()
      .toUpperCase()

  const isPartiallyAnuladoStatus = status => {
    const normalized = normalizeStatus(status)
    return normalized.includes('PARCIAL') && normalized.includes('ANUL')
  }

  const isStatusActionBlocked = status => {
    const normalized = normalizeStatus(status)
    return normalized.includes('ANUL') && !isPartiallyAnuladoStatus(normalized)
  }

  const isEditActionBlocked = status => {
    const normalized = normalizeStatus(status)
    const canEditAnulado =
      normalized.includes('ANULA FOLIO') || normalized.includes('ANULADO POR')
    return !canEditAnulado && isStatusActionBlocked(normalized)
  }

  const statusTone = status => {
    const s = normalizeStatus(status)
    if (s.includes('PARCIAL') && s.includes('PAGAD')) return 'warning'
    if (s.includes('PARCIAL') && s.includes('ANUL')) return 'muted'
    if (s.includes('ANUL')) return 'muted'
    if (s.includes('PAGADO')) return 'success'
    if (s.includes('POR PAGAR')) return 'danger'
    return 'muted'
  }

  const statusBadgeClass = status => {
    const tone = statusTone(status)
    return clsx(
      'inline-flex items-center justify-center px-3 py-1 rounded-full text-[0.55rem] sm:text-[0.6rem] font-semibold uppercase tracking-wider leading-tight',
      {
        'text-danger bg-danger/10': tone === 'danger',
        'text-success bg-success/10': tone === 'success',
        'text-amber-700 bg-amber-100': tone === 'warning',
        'text-muted bg-border/40': tone === 'muted'
      }
    )
  }

  const getCentroCostoValue = row => row?.centroCosto ?? row?.centroCostos ?? null

  const hasCentroCostoAssignment = centroCosto => {
    if (Array.isArray(centroCosto)) {
      return centroCosto.some(item => {
        if (item == null) return false
        if (typeof item === 'object') return Object.keys(item).length > 0
        return String(item).trim() !== ''
      })
    }

    if (centroCosto && typeof centroCosto === 'object') {
      return Object.keys(centroCosto).length > 0
    }

    if (typeof centroCosto === 'string') {
      const normalized = centroCosto.trim()
      return normalized !== '' && normalized !== '-' && normalized !== '[]'
    }

    return Boolean(centroCosto)
  }

  const rowHasCentroCosto = row => hasCentroCostoAssignment(getCentroCostoValue(row))

  const centroCostoBadgeClass = hasCentroCosto =>
    clsx(
      'inline-flex items-center justify-center min-w-[3.25rem] px-3 py-1 rounded-full whitespace-nowrap text-[0.55rem] sm:text-[0.6rem] font-semibold tracking-wider leading-tight',
      {
        'text-accent2 bg-accent2/10': hasCentroCosto,
        'text-danger bg-danger/10': !hasCentroCosto
      }
    )

  const renderCentroCostoBadge = hasCentroCosto => (
    <span className={centroCostoBadgeClass(hasCentroCosto)}>
      {hasCentroCosto ? 'ASIGNADO' : 'NO ASIGNADO'}
    </span>
  )

  const renderRowAmount = row => {
    if (row.nombreFolio === 'Boleta') {
      return (
        <>
          <Text className='text-sm font-bold text-right'>
            {formatCurrency(row.montoNetoLiquido)}
          </Text>
          <Text className='text-right text-xs text-accent3'>
            Retención: {formatCurrency(row.montoIvaRetenido)}
          </Text>
          <Text className='text-right text-xs text-gray-500'>
            Total Honorario: {formatCurrency(row.montoTotal)}
          </Text>
        </>
      )
    }
    return (
      <>
        <Text className='text-sm font-bold text-right'>
          {formatCurrency(row.montoTotal)}
        </Text>
        <Text className='text-right text-xs text-accent3'>
          IVA: {formatCurrency(row.montoIvaRetenido)}
        </Text>
        {(row.montoNetoLiquido !== 0 || row.montoExento == null) && (
          <Text className='text-right text-xs text-gray-500'>
            Neto: {formatCurrency(row.montoNetoLiquido)}
          </Text>
        )}
        {row.montoExento != null && row.montoExento !== 0 && (
          <Text className='text-xs text-right text-gray-500'>
            Exento: {formatCurrency(row.montoExento)}
          </Text>
        )}
      </>
    )
  }

  const renderRowCard = row => {
    const cleanStatus = getCleanStatus(row)
    const hasCentroCosto = rowHasCentroCosto(row)
    const isAnulado =
      typeof row.status === 'string' &&
      (row.status.includes('ANULADO') || row.status.includes('ANULA'))
    const isActionBlocked = isStatusActionBlocked(row.status)
    const isEditBlocked = isEditActionBlocked(row.status)
    const isNotaCredito = row.nombreFolio === 'Nota de Crédito Electrónica'
    const canConciliar = !isActionBlocked && !isNotaCredito && row.aprobado !== -5
    return (
      <div className='rounded-2xl border border-gray-100 bg-white shadow-sm p-3'>
        <div className='flex items-start justify-between gap-2'>
          <div className='flex items-start gap-2'>
            {highlightedRows[row.id] ? (
              <StarFullIcon
                style={{ color: '#FFD700', cursor: 'pointer' }}
                onClick={() => toggleHighlight(row.id)}
              />
            ) : (
              <StarIcon
                style={{ color: '#a1a1aa', cursor: 'pointer' }}
                onClick={() => toggleHighlight(row.id)}
              />
            )}
            <div>
              <Text className='text-xs font-bold'>
                {obtenerAbreviatura(row.nombreFolio)} · {row.numeroFolio}
              </Text>
              <Text className='text-[11px] text-gray-500'>
                {(row.rut && formatRut(row.rut)) || '<RUT>'}
              </Text>
            </div>
          </div>
          <span className={statusBadgeClass(cleanStatus)}>
            {normalizeStatus(cleanStatus) || '—'}
          </span>
        </div>

        <div className='mt-2'>
          <Text className='text-xs font-semibold'>
            {row.razonSocial || '<Razon Social>'}
          </Text>
        </div>

        <div className='mt-2 grid grid-cols-2 gap-2 text-xs'>
          <div>
            <Text className='text-[10px] text-gray-400'>Fecha</Text>
            <Text className='text-xs font-semibold'>
              {row.fecha && formatDate(row.fecha)}
            </Text>
            <Text className='text-[11px] text-gray-500'>
              {row.fechaVencimiento && formatDate(row.fechaVencimiento)}
            </Text>
            <Text className='text-[11px] text-gray-500'>{row.diasHastaVencimiento}</Text>
          </div>
          <div className='text-right'>{renderRowAmount(row)}</div>
        </div>

        <div className='mt-2'>
          <Text className='text-[10px] text-gray-400'>Clasificación</Text>
          <Text className='text-xs font-semibold'>{row.nombreCuenta}</Text>
          <Text className='text-[11px] text-gray-500'>
            {row.codigoCuenta}{' '}
            <Link
              onClick={() => handleComprobante(row.id, row.nombreFolio, row.folio, 'compra')}
              style={{ cursor: 'pointer' }}
            >
              {row.comprobante || 'ID'}
            </Link>
            {' @' + (row.usuarioClasificador || 'TryLuca')}
          </Text>
        </div>

        <div className='mt-2 flex items-center justify-between gap-3'>
          <Text className='text-[10px] text-gray-400'>Centro costo</Text>
          {renderCentroCostoBadge(hasCentroCosto)}
        </div>

        <div className='mt-3 flex items-center justify-between'>
          <button
            type='button'
            className='flex items-center justify-center w-9 h-9 rounded-full bg-gray-100'
            onClick={() => handleClickOpen(row.id, row.status, row.nombreFolio)}
            disabled={isEditBlocked}
            aria-label='Editar movimiento'
          >
            <EditOutlinedIcon sx={{ color: 'inherit', fontSize: 16 }} />
          </button>
          <div className='flex items-center gap-2'>
            <button
              type='button'
              onClick={() => canConciliar && handleClickConciliar(row.id, row.status, row.nombreFolio)}
              className={clsx(
                'flex items-center justify-center w-9 h-9 rounded-full shadow-full-xs',
                {
                  'cursor-not-allowed opacity-50': !canConciliar,
                  'bg-accent2 text-white': row.status === 'PAGADO',
                  'bg-white text-slate-400': row.status !== 'PAGADO'
                }
              )}
            >
              <img
                src={iconoEnlace}
                alt='icono enlace'
                className={clsx('w-5 h-5', { 'brightness-200': row.status === 'PAGADO' })}
              />
            </button>
            <Text className='text-[11px] text-gray-500'>
              {row.status === 'PAGADO' ? 'Conciliado' : 'Conciliar'}
            </Text>
          </div>
          <button
            type='button'
            onClick={() => {
              if (!isAnulado) handleNoConsiderarClick(row)
            }}
            className={clsx(
              'flex items-center justify-center w-9 h-9 rounded-full shadow-full-xs',
              {
                'cursor-not-allowed opacity-50': isAnulado,
                'bg-red-500 text-white': row.aprobado === -5,
                'bg-gray-200 text-slate-400': row.aprobado !== -5
              }
            )}
          >
            <BlockIcon sx={{ fontSize: 20 }} />
          </button>
        </div>
      </div>
    )
  }

  if (!rows?.[0])
    return (
      <div className="flex flex-col items-center py-12">
        <SearchIcon fontSize="large" className="mb-4 text-gray-400" />
        <Text>{translations.tableAction.noData}</Text>
      </div>
    )
  else if (isMobile)
    return (
      <div className='space-y-3 font-sans'>
        {rows.map((row, i) => (
          <div key={row?.id ?? i}>{renderRowCard(row)}</div>
        ))}
        {open && (
          <ModalDialogMotionEditForm
            title='Editar movimiento'
            open={open}
            setOpen={setOpen}
            idLibro={dataModal.idLibro}
            status={dataModal.status}
            type={resolveLibroType(dataModal.documentType)}
            setSwitchGetData={setSwitchGetData}
          />
        )}
        {openConciliar && (
          <ModalEditConciliar
            open={openConciliar}
            setOpen={setOpenConciliar}
            idLibro={dataModal.idLibro}
            status={dataModal.status}
            type={resolveLibroType(dataModal.documentType)}
            setSwitchGetData={setSwitchGetData}
          />
        )}
        {openComprobante && (
          <ModalViewAdministracion
            openModal={openComprobante}
            setOpenModal={setOpenComprobante}
            comprobanteId={dataComprobante.libroId}
            validation={1}
            folio={dataComprobante.nombreFolio}
            numeroFolio={dataComprobante.folio}
          />
        )}
        {showPopup && (
          <div className='fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50'>
            <div className='relative bg-white px-6 py-5 rounded-xl shadow-2xl w-[320px] text-center'>
              <div className='flex justify-center mb-4'>
                <div className='w-16 h-16 border-2 border-red-500 rounded-full flex items-center justify-center'>
                  <span className='text-red-500 text-4xl'>!</span>
                </div>
              </div>
              <h2 className='font-sans text-base text-gray-800 mt-2'>
                {pendingApproval
                  ? '¿Estás seguro de que deseas aprobar masivamente los movimientos?'
                  : '¿Estás seguro de que deseas desaprobar masivamente los movimientos?'}
              </h2>
              <p className='text-sm text-gray-600 mt-3'>
                Esta acción tiene impacto en el algoritmo de clasificación.
              </p>
              <div className='flex justify-center gap-4 mt-6'>
                <CustomButton title='Cancelar' color='primary' className='rounded-full' onClick={() => setShowPopup(false)} />
                <CustomButton title='Aceptar' color='accent3' className='rounded-full' onClick={confirmAction} />
              </div>
            </div>
          </div>
        )}
      </div>
    )
  else
    return (
      <div className='font-sans'>
          <table className={styles.table}>
            <thead className={styles.thead}>
              <tr>
                {orderableCols.map(({ col, field }, i) => (
                  <th key={i} 
                  className={clsx(
                    styles.th,
                    { 'cursor-pointer': field },
                    i === 0 && styles.smallCol
                  )}
                  onClick={() => field && onSort({ col, field })}>
                  {col}
                  {order.col === col && (
                    <KeyboardArrowDownIcon
                      className={clsx('transition-all ml-2 cursor-pointer', {
                        'opacity-40': order.col !== col,
                        'rotate-180': order.direction === 'desc'
                      })}
                    />
                  )}
                </th>
                ))}
              </tr>
            </thead>
            <tbody className={styles.tbody}>
              {rows.map((row, i) => {
                const cleanStatus =
                  typeof row.status === 'string'
                    ? row.status.replace(/\s*undefined\s*/gi, '').trim()
                    : ''
                const hasCentroCosto = rowHasCentroCosto(row)
                const isActionBlocked = isStatusActionBlocked(row.status)
                const isEditBlocked = isEditActionBlocked(row.status)

                return (
                  <tr className={clsx(styles.tr)} key={i}>
                  {/* Nombre origen */}
                  <td className={clsx(styles.td, styles.smallCol)}>
                    <div className='flex items-center'>
                      {highlightedRows[row.id] ? (
                          <StarFullIcon
                            style={{ color: '#FFD700', cursor: 'pointer' }}
                            onClick={() => toggleHighlight(row.id)}
                          />
                        ) : (
                          <StarIcon
                            style={{ color: '#a1a1aa', cursor: 'pointer' }}
                            onClick={() => toggleHighlight(row.id)}
                          />
                        )}
                      <div className='relative'>
                       {currentDate === formatDate(row.fecha) ? (
                         <div className={clsx(styles.td, styles.smallCol)}>
                           <img src={isologo_SII} className='h-5 w-auto rounded mt-2' />
                           <img
                             src={iconNuevo}
                             className='relative z-auto bottom-8 left-5 w-5 h-5 opacity-60'
                           />
                         </div>
                       ) : (
                         <div className={clsx(styles.td, styles.smallCol)}>
                           <img src={isologo_SII} className='h-5 w-auto rounded mt-2' />
                         </div>
                       )}
                      </div>
                    </div>
                  </td>
                  {/* Folio */}
                  <td className={styles.td}>
                    <Text className={styles.boldText}>{obtenerAbreviatura(row.nombreFolio)}</Text>
                    {row.query && row.numeroFolio && pdfAvailabilityByKey[getPdfSummaryKey(row)] ? (
                      <Text className={styles.pdfLink}>
                        <Link
                          onClick={() => handleOpenDocumentoPdf(row)}
                          className={styles.pdfLink}
                          style={{
                            cursor: pdfLoadingId === row.id ? 'not-allowed' : 'pointer',
                            opacity: pdfLoadingId === row.id ? 0.4 : 1
                          }}
                          title='Ver PDF'
                        >
                          {pdfLoadingId === row.id ? <Spinner /> : row.numeroFolio}
                        </Link>
                      </Text>
                    ) : (
                      <Text className={styles.text}>{row.numeroFolio}</Text>
                    )}
                  </td>
                  {/* Cliente */}
                  <td className={clsx(styles.td)}>
                    <Text className={styles.boldText}>{row.razonSocial || '<Razon Social>'}</Text>
                    <Text className={styles.text}>
                      {(row.rut && formatRut(row.rut)) || '<RUT>'}
                    </Text>
                  </td>
                  {/* Fecha */}
                  <td className={styles.td}>
                    <Text className={styles.boldText}>{row.fecha && formatDate(row.fecha)}</Text>
                    <Text className={styles.text}>
                      {row.fechaVencimiento && formatDate(row.fechaVencimiento)}
                    </Text>
                    <Text className={styles.text}>{row.diasHastaVencimiento}</Text>
                  </td>
                  {/* Clasificacion */}
                   <td className={styles.td}>
                    <div className='flex items-center justify-center gap-2'>
                      <div className='min-w-0 flex-1'>
                        <Text className={styles.boldText}>{row.nombreCuenta}</Text>
                        <Text className={styles.text}>{row.codigoCuenta}</Text>
                        <Text className={styles.text}>
                          <Link
                            onClick={() => handleComprobante(row.id, row.nombreFolio, row.folio, 'compra')}
                            className={styles.text}
                            style={{ cursor: 'pointer' }}
                          >
                            {row.comprobante || 'ID'}
                          </Link>
                          {' @' + (row.usuarioClasificador || 'TryLuca')}
                        </Text>
                      </div>
                    </div>
                </td>
                 <td className={styles.td}>
                    <Text className={clsx(styles.boldText, 'text-right')}>{renderCentroCostoBadge(hasCentroCosto)}</Text>
                </td>
                  {/* Monto */}
                  <td className={styles.td}>
                    {row.nombreFolio === 'Boleta' ? (
                      <>
                        <Text className={clsx(styles.boldText, 'text-xs text-right font-bold')}>
                          {formatCurrency(row.montoNetoLiquido)}
                        </Text>
                        <Text className='text-right text-accent3'>
                          Retención: {formatCurrency(row.montoIvaRetenido)}
                        </Text>
                        <Text className='text-right text-gray-500'>
                          Total Honorario: {formatCurrency(row.montoTotal)}
                        </Text>
                      </>
                    ) : (
                      <>
                        <Text className={clsx(styles.boldText, 'text-xs  text-right font-bold')}>
                          {formatCurrency(row.montoTotal)}
                        </Text>
                        <Text className='text-right text-accent3'>
                          IVA: {formatCurrency(row.montoIvaRetenido)}
                        </Text>
                        {(row.montoNetoLiquido !== 0 || row.montoExento == null) && (
                          <Text className='text-right text-gray-500'>
                            Neto: {formatCurrency(row.montoNetoLiquido)}
                          </Text>
                        )}
                      </>
                    )}
                    {row.montoExento != null && row.montoExento !== 0 && (
                      <Text className='text-xs text-right text-gray-500'>
                        Exento: {formatCurrency(row.montoExento)}
                      </Text>
                    )}
                  </td>
                  {/* Status */}
                  <td className={styles.td}>
                    <span className={statusBadgeClass(cleanStatus)}>
                      {normalizeStatus(cleanStatus) || '—'}
                    </span>
                  </td>
                  {/* Acciones */}
                  <td className={styles.td}>
                    <div className="flex h-full items-center justify-center space-x-2">
                      <div className="flex flex-col text-xs text-gray-500 items-center justify-center">
                        <div
                          onClick={() => {
                            const isActionBlockedForConciliar = isActionBlocked;
                            const isNotaCredito = row.nombreFolio === 'Nota de Crédito Electrónica';

                            if (!isActionBlockedForConciliar && !isNotaCredito && row.aprobado !== -5) {
                              handleClickConciliar(row.id, row.status, row.nombreFolio);
                            }
                          }}
                         className={clsx(
                            'flex text-slate-400 shadow-full-xs rounded-full items-center justify-center w-8 h-8 transition-colors duration-150',
                            {
                              'cursor-not-allowed opacity-50':
                                isActionBlocked ||
                                row.nombreFolio === 'Nota de Crédito Electrónica',
                              'hover:bg-slate-100/50 cursor-pointer':
                                !(
                                  isActionBlocked ||
                                  row.nombreFolio === 'Nota de Crédito Electrónica' 
                                ),
                              'bg-accent2 text-white': row.status === 'PAGADO',
                              'bg-[#fff]': row.status !== 'PAGADO',
                            }
                          )}
                        >
                            <img
                              src={iconoEnlace}
                              alt="icono enlace"
                              className={clsx('w-5 h-5 transition-all duration-300', {
                                'brightness-200': row.status === 'PAGADO',
                              })}
                            />
                        </div>
                        <Text className="mt-2">{row.status === 'PAGADO' ? 'Conciliado' : 'Conciliar'}</Text>
                      </div>
                      <div className="flex flex-col text-xs text-gray-500 items-center justify-center">
                        <button
                          type='button'
                          aria-label='Editar'
                          disabled={isEditBlocked}
                          onClick={() => handleClickOpen(row.id, row.status, row.nombreFolio)}
                          className='flex text-slate-400 shadow-full-xs rounded-full items-center justify-center w-8 h-8 transition-colors duration-150 bg-white hover:bg-slate-100/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white'
                        >
                          <EditOutlinedIcon sx={{ color: 'inherit', fontSize: 20 }} />
                        </button>
                        <Text className="mt-2">Editar</Text>
                      </div>
                      <div className="flex flex-col text-xs text-gray-500 items-center justify-center">
                        <button
                          onClick={() => {
                            const isAnulado = typeof row.status === 'string' && row.status.includes('ANULADO');
                            if (!isAnulado) {
                              handleNoConsiderarClick(row);
                            }
                          }}
                          className={`
                            ${row.aprobado === -5 ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-gray-200 hover:bg-red-100 text-slate-400' }
                            ${ typeof row.status === 'string' && row.status.includes('ANULADO') ? 'cursor-not-allowed opacity-50' : 'cursor-pointer' }
                            flex shadow-full-xs rounded-full items-center justify-center w-8 h-8 transition-colors duration-150
                          `}
                        >
                          <BlockIcon sx={{fontSize: 24 }}/>
                        </button>
                        <Text className="mt-2">
                          {row.aprobado === -5
                            ? translations.tableAction.notConsidered
                            : translations.tableAction.considered}
                        </Text>
                      </div>
                    </div>
                  </td>
                </tr>
            )})}
              {emptyRows > 0 && (
                <tr className={styles.tr} style={{ height: 89 * emptyRows }}>
                  <td className={styles.td} colSpan={cols?.length} aria-hidden />
                </tr>
              )}
            </tbody>
          </table>
        {open && (
          <ModalDialogMotionEditForm
            title='Editar movimiento'
            open={open}
            setOpen={setOpen}
            idLibro={dataModal.idLibro}
            status={dataModal.status}
            type={resolveLibroType(dataModal.documentType)}
            setSwitchGetData={setSwitchGetData}
          />
        )}
        {openConciliar && (
            <ModalEditConciliar
              open={openConciliar}
              setOpen={setOpenConciliar}
              idLibro={dataModal.idLibro}
              status={dataModal.status}
              type={resolveLibroType(dataModal.documentType)}
              setSwitchGetData={setSwitchGetData}
            />
        )}
        {openComprobante && (
           <ModalViewAdministracion
           openModal={openComprobante}
           setOpenModal={setOpenComprobante}
           comprobanteId={dataComprobante.libroId}
           validation={1} // O ajusta según lo que necesites
           folio={dataComprobante.nombreFolio}
           numeroFolio={dataComprobante.folio}
         />
        )}
         {showPopup && (
          <div className='fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50'>
            <div className='relative bg-white px-8 py-6 rounded-xl shadow-2xl w-[400px] text-center'>
              <div className='flex justify-center mb-4'>
                <div className='w-16 h-16 border-2 border-red-500 rounded-full flex items-center justify-center'>
                  <span className='text-red-500 text-4xl'>!</span>
                </div>
              </div>
              <h2 className='font-sans text-base text-gray-800 mt-2'>
                {pendingApproval
                  ? '¿Estás seguro de que deseas aprobar masivamente los movimientos?'
                  : '¿Estás seguro de que deseas desaprobar masivamente los movimientos?'}
              </h2>
              <p className='text-sm text-gray-600 mt-3'>
                Esta acción tiene impacto en el algoritmo de clasificación.
              </p>
              <div className='flex justify-center gap-4 mt-6'>
              <CustomButton title='Cancelar' color='primary' className='rounded-full' onClick={() => setShowPopup(false)} />
                <CustomButton title='Aceptar' color='accent3' className='rounded-full' onClick={confirmAction} />
              </div>
            </div>
          </div>
        )}
      </div>
    )
}

export default TableEgresos
